/*
 * ******************************************************************************
 * Copyright Innov'ATM all rights reserved. This software is the property of
 * Innov'ATM and may not be used in any manner except under a license agreement
 * signed with Innov'ATM.
 * *******************************************************************************
 */

import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { createContext, useContext, useContextSelector } from 'use-context-selector';

import { getStoredValue, useSetStoredValue } from '../hooks/usePersistedState';
import { contextStorageKey, defaultContext } from '../utils/utils';

export interface Ui {
    context: string;
}

export const initialUi: Ui = {
    context: getStoredValue(contextStorageKey, defaultContext),
};

export interface UiState {
    ui: Ui;
    setUi: React.Dispatch<React.SetStateAction<Ui>>;
}

const initialValue: UiState = {
    ui: initialUi,
    setUi: () => {
        /**/
    },
};

export const UiContext = createContext<UiState>(initialValue);

export const useUi = () => useContext(UiContext);

export const useUiSet = () => useContextSelector(UiContext, state => state.setUi);

export const useUiValue = <Key extends keyof Ui>(key: Key): Ui[Key] =>
    useContextSelector(UiContext, state => state.ui[key]);

export const useSetUi = <Key extends keyof Ui>(key: Key) => {
    const set = useUiSet();

    return useCallback(
        (value: React.SetStateAction<Ui[Key]>) =>
            set(oldValue => {
                const newValue = typeof value === 'function' ? (value as any)(oldValue[key]) : value;

                return {
                    ...oldValue,
                    [key]: newValue,
                };
            }),
        [key, set],
    );
};

export const useSetUiValue = <Key extends keyof Ui>(key: Key, value: React.SetStateAction<Ui[Key]>) => {
    const set = useSetUi(key);

    return useCallback(() => set(value), [set, value]);
};

export const useUiState = <Key extends keyof Ui>(
    key: Key,
): [Ui[Key], React.Dispatch<React.SetStateAction<Ui[Key]>>] => [useUiValue(key), useSetUi(key)];

export const UiContextProvider = ({ children }: { children: React.ReactNode }) => {
    const [ui, setUi] = useState<Ui>(initialUi);

    // sync value
    const setContext = useSetStoredValue(contextStorageKey);
    useEffect(() => {
        setContext(ui.context);
    }, [setContext, ui.context]);

    return <UiContext.Provider value={{ ui, setUi }}>{children}</UiContext.Provider>;
};
